/**
 * Shared content-shaped loading placeholder — the one Skeleton for every app
 * (BL-1414, ratifying the `Skeleton` row of the ADR 030 Canon).
 *
 * Moved from bidder-web (BL-714), which is the canonical base per ADR 030
 * Decision 3: rect / text / circle variants, an 8px default corner, a
 * left-to-right shimmer sweep, a reduced-motion opt-out, and multi-line text
 * that shortens its last bar. admin-web's copy was a bare `animate-pulse`
 * block that pushed every sizing decision back onto each call site; it is
 * deleted, and its single caller re-pointed here.
 *
 * Same shape as `Spinner`: this component sizes the box and sets the class;
 * the shimmer itself lives in `@auctogon/ui/styles/skeleton.css`, which each
 * app imports once from its root layout. The base grey arrives as the
 * `--ag-skeleton-bg` custom property each app sets in its `globals.css` —
 * bidder binds `--bw-skeleton`, admin its `gray-100` — so the dialect is a
 * token value, not a wrapper file.
 *
 * Purely presentational + `aria-hidden`: the enclosing loading surface owns
 * the `role="status"` announcement. Reach for this instead of a lone spinner
 * on any data surface so content swaps in without a layout shift.
 */
'use client';

import clsx from 'clsx';
import type { CSSProperties } from 'react';

/** Placeholder shapes. */
export type SkeletonVariant = 'rect' | 'text' | 'circle';

/** Props for {@link Skeleton}. */
export interface SkeletonProps {
    /** Placeholder shape. `text` renders `lines` bars; `circle` is a round dot. Default `rect`. */
    variant?: SkeletonVariant;
    /** Width — number (px) or any CSS length. Circle uses this as its diameter. */
    width?: number | string;
    /** Height — number (px) or any CSS length. Ignored for `circle` (uses width). */
    height?: number | string;
    /** Corner radius — number (px) or CSS length. Ignored for `circle` (always round). */
    radius?: number | string;
    /** Number of bars for the `text` variant. Default 1. The last bar is shortened. */
    lines?: number;
    /** Extra classes on the outer element (sizing utilities are fine). */
    className?: string;
}

/** Coerce a number to a px string; pass strings through untouched. */
function len(v: number | string | undefined): string | undefined {
    if (v == null) return undefined;
    return typeof v === 'number' ? `${v}px` : v;
}

/**
 * A shimmering placeholder bar/box/dot shown while data loads.
 *
 * @param props see {@link SkeletonProps}
 * @returns an `aria-hidden` placeholder carrying the `.ag-skeleton` shimmer
 */
export function Skeleton({
    variant = 'rect',
    width,
    height,
    radius,
    lines = 1,
    className,
}: SkeletonProps) {
    if (variant === 'text') {
        const count = Math.max(1, lines);
        return (
            <div
                data-testid="skeleton"
                data-variant="text"
                aria-hidden="true"
                className={clsx('flex flex-col gap-2', className)}
                style={{ width: len(width) }}
            >
                {Array.from({ length: count }).map((_, i) => (
                    <span
                        key={i}
                        data-testid="skeleton-bar"
                        className="ag-skeleton block h-[0.9em] rounded"
                        style={{ width: i === count - 1 && count > 1 ? '75%' : '100%' }}
                    />
                ))}
            </div>
        );
    }

    const isCircle = variant === 'circle';
    const style: CSSProperties = isCircle
        ? { width: len(width) ?? '40px', height: len(width) ?? '40px', borderRadius: '9999px' }
        : { width: len(width), height: len(height), borderRadius: len(radius) ?? '8px' };

    return (
        <span
            data-testid="skeleton"
            data-variant={variant}
            aria-hidden="true"
            className={clsx('ag-skeleton block', className)}
            style={style}
        />
    );
}
